import axios from "axios";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { User } from "oidc-client-ts";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentBrowserFingerPrint } from "@rajesh896/broprint.js";
import { createContext, useContext, useEffect, useState } from "react";
import { browserName, browserVersion, deviceType, osVersion, osName } from "react-device-detect";

import Helpers from "commons/helpers";
import Constants from "constants/index";
import Strings from "constants/strings";
import Screens from "constants/screens";

import { RootState } from "store";
import { UserManager } from "commons/userManager";
import { ISessionUser } from "commons/interfaces";
import { fetchUser, resetUser } from "store/slice/user.slice";
import { resetAuth, setAuthInfo } from "store/slice/auth.slice";



interface AuthContextType {
    loading: boolean;
    user?: ISessionUser | null;
    signoutRedirect: () => Promise<void>;
    onSwitchAccount: () => Promise<void>;
    signinRedirect: (params?: { [key: string]: string | number | boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(null!);
const useAuth = () => useContext(AuthContext);
export const userManager = UserManager();

function AuthProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();

    const currentUser = useSelector((state: RootState) => state.auth.authInfo);

    const [loading, setLoading] = useState(currentUser === undefined);


    useEffect(() => {
        const handleSignin = async () => {
            const searchParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.replace("#", "?"));
            try {
                if (
                    searchParams.get("code") ||
                    searchParams.get("id_token") ||
                    searchParams.get("session_state") ||
                    hashParams.get("code") ||
                    hashParams.get("id_token") ||
                    hashParams.get("session_state")
                ) {
                    const user = (await userManager.signinCallback()) || null;

                    dispatch(setAuthInfo({ ...user }));

                    axios.defaults.headers["Authorization"] = "Bearer " + user?.access_token;

                    if (!Helpers.isNullOrEmpty(user)) {
                        dispatch(fetchUser());
                    }
                } else if (loading) {
                    await userManager.signinSilent();
                }
            } catch (error) {
                dispatch(setAuthInfo(null));
                if (window.location.pathname === Screens.LOGIN_REDIRECT) {
                    signinRedirect();
                }
            }
        };
        handleSignin();

        __EventEmitter.addListener(Constants.EventName.TOKEN_EXPIRED, handleTokenExpired);

        return () => {
            __EventEmitter.removeListener(Constants.EventName.TOKEN_EXPIRED, handleTokenExpired);
        };
    }, []);

    useEffect(() => {
        if (currentUser !== undefined) {
            setLoading(false);

            if (currentUser) {
                handleLoadActiveUser();
            }
        }
    }, [currentUser]);

    useEffect(() => {
        if (!userManager) {
            return;
        }

        const handleUserLoaded = async (newUser: User) => {
            if (newUser) {
                dispatch(setAuthInfo({ ...newUser }));

                axios.defaults.headers.common["Authorization"] = "Bearer " + newUser?.access_token;

                dispatch(fetchUser());
            } else {
                dispatch(setAuthInfo(null));
            }
        };

        userManager.events.addUserLoaded(handleUserLoaded);
        userManager.events.addSilentRenewError(handleTokenExpired);

        return () => {
            userManager.events.removeUserLoaded(handleUserLoaded);
            userManager.events.removeSilentRenewError(handleTokenExpired);
        };
    }, [userManager]);

    const handleTokenExpired = () => {
        Helpers.showAlert(Strings.MESSAGE.TOKEN_EXPIRED, "error", async () => {
            await signoutRedirect();
        });
    };

    const handleLoadActiveUser = async () => {
        const searchParams = new URLSearchParams(window.location.search);
        const activeUser = searchParams.get("activeUser");
        if (activeUser && activeUser !== currentUser.profile?.sub) {
            signoutLocal();
            await signinRedirect({ active_user: activeUser });
        }
    };

    const signinRedirect = async (params?: { [key: string]: string | number | boolean }) => {
        try {
            const deviceId = await getDeviceId();
            const deviceInfo = JSON.stringify({
                browserName,
                browserVersion,
                deviceType,
                osVersion,
                osName,
            });

            let extraQueryParams: { [key: string]: string | number | boolean } = {
                deviceId,
                deviceInfo,
            };

            if (!Helpers.isNullOrEmpty(extraQueryParams)) {
                extraQueryParams = {
                    ...extraQueryParams,
                    ...params,
                };
            }

            await userManager.signinRedirect({ extraQueryParams });
        } catch (error) {
            dispatch(setAuthInfo(null));
        }
    };

    const signoutRedirect = async () => {
        const idTokenHint = currentUser?.id_token;
        dispatch(resetUser());
        dispatch(resetAuth());
        sessionStorage.clear();
        localStorage.clear();
        const origin = window.location.origin;
        await userManager.signoutRedirect({
            id_token_hint: idTokenHint,
            post_logout_redirect_uri: origin + Constants.POST_LOGOUT_REDIRECT_URL,
        });
    };

    const onSwitchAccount = async () => {
        signoutLocal();
        await signinRedirect({ switchUser: 1 });
    };

    const signoutLocal = () => {
        sessionStorage.clear();
        localStorage.clear();
    };

    const getDeviceId = async () => {
        try {
            const result = await getCurrentBrowserFingerPrint();
            return result;
        } catch (error) {
            return uuidv4();
        }
    };

    const value = {
        user: currentUser,
        loading,
        signinRedirect,
        signoutRedirect,
        onSwitchAccount,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthProvider, useAuth };
